import React, { useState, useEffect, useMemo } from 'react';
import useApi from '../hooks/useApi';
import haxios from '../lib/haxios';
import {
    PencilSquareIcon,
    TrashIcon,
    PlusIcon,
    UserIcon
} from '@heroicons/react/24/outline';

export default function DriversPage() {
    const { data: driversResponse, refetch, isLoading } = useApi({ url: `/visiondashboard/salesassist/drivers`, initial: { data: [] } });
    const { data: branches } = useApi({ url: `/salesassist/cplusassist/branches`, initial: [] });
    const { data: operatorsResponse } = useApi({ url: `/visiondashboard/salesassist/operators`, initial: [] });

    const drivers = driversResponse?.data || [];
    const operators = operatorsResponse?.data || [];
    console.info('drivers:', drivers);
    console.info('operators (raw):', operatorsResponse);

    const [formData, setFormData] = useState({ branch_id: '', staff_id: '', license_number: '', license_expiry: '', penalty_points: 0 });
    const [isEditing, setIsEditing] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [filteredOperators, setFilteredOperators] = useState([]);

    useEffect(() => {
        if (formData.branch_id) {
            const filtered = operators.filter(operator => operator.Branch === formData.branch_id);
            setFilteredOperators(filtered);
        } else {
            setFilteredOperators([]);
        }
    }, [formData.branch_id, operators]);

    useEffect(() => {
        console.info('filteredOperators (for branch', formData.branch_id, '):', filteredOperators);
    }, [filteredOperators, formData.branch_id]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        console.info('formData:', formData);
        if (!formData.branch_id || !formData.staff_id || !formData.license_number || !formData.license_expiry) {
            alert('Branch, Staff ID, License Number, and Expiry Date are required.');
            return;
        }

        try {
            if (isEditing) {
                await haxios.put(`/visiondashboard/salesassist/drivers/${formData.staff_id}`, formData);
            } else {
                await haxios.post(`/visiondashboard/salesassist/drivers`, formData);
            }
            refetch();
            resetForm();
        } catch (error) {
            console.error('Error saving driver:', error);
            alert('Failed to save driver.');
        }
    };

    const handleEdit = (driver) => {
        setFormData({
            ...driver,
            license_expiry: driver.license_expiry.split('T')[0]
        });
        setIsEditing(true);
        setShowForm(true);
    };

    const handleDelete = async (staff_id) => {
        if (!window.confirm('Are you sure you want to delete this driver?')) return;
        try {
            await haxios.delete(`/visiondashboard/salesassist/drivers/${staff_id}`);
            refetch();
        } catch (error) {
            console.error('Error deleting driver:', error);
            alert('Failed to delete driver.');
        }
    };

    const resetForm = () => {
        setFormData({ branch_id: '', staff_id: '', license_number: '', license_expiry: '', penalty_points: 0 });
        setIsEditing(false);
        setShowForm(false);
        setFilteredOperators([]);
    };

    const getStaffName = (staff_id) => {
        const staff = operators.find(op => op.staffId === staff_id);
        return staff ? staff.Name : staff_id;
    };

    // Calculate KPIs
    const kpis = useMemo(() => {
        const totalDrivers = drivers.length;
        const expiredLicenses = drivers.filter(driver => {
            const expiryDate = driver.license_expiry ? new Date(driver.license_expiry) : null;
            if (!expiryDate) return false;
            return expiryDate < new Date();
        }).length;
        const expiringSoon = drivers.filter(driver => {
            const expiryDate = driver.license_expiry ? new Date(driver.license_expiry) : null;
            if (!expiryDate) return false;
            const daysUntilExpiry = (expiryDate - new Date()) / (1000 * 60 * 60 * 24);
            return daysUntilExpiry >= 0 && daysUntilExpiry <= 60;
        }).length;
        const withPenaltyPoints = drivers.filter(driver => (driver.penalty_points || 0) > 0).length;
        return { totalDrivers, expiredLicenses, expiringSoon, withPenaltyPoints };
    }, [drivers]);

    // Set document title
    useEffect(() => {
        document.title = 'Fleet Drivers - Sales Assist';
        return () => {
            document.title = 'Sales Assist';
        };
    }, []);

    return (
        <div className="min-h-screen bg-gray-50 pt-20">
            <div className="py-6">
                <div className="px-6">
                    {/* Header */}
                    <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4 mb-6 -mx-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="p-2 rounded-lg bg-green-100">
                                    <UserIcon className="w-6 h-6 text-green-600" />
                                </div>
                                <div>
                                    <h1 className="text-2xl font-bold text-gray-900">Fleet Drivers</h1>
                                    <p className="text-sm text-gray-600 mt-1">Manage driver licenses and information</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowForm(!showForm)}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <PlusIcon className="w-5 h-5" />
                                {showForm ? 'Cancel' : 'Add Driver'}
                            </button>
                        </div>
                    </div>

                    {/* KPI Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-blue-500">
                            <div className="text-sm font-medium text-gray-600 mb-1">Total Drivers</div>
                            <div className="text-2xl font-bold text-gray-900">{kpis.totalDrivers.toLocaleString('en-GB')}</div>
                            <div className="text-xs text-gray-500 mt-1">All drivers</div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-red-500">
                            <div className="text-sm font-medium text-gray-600 mb-1">Expired Licenses</div>
                            <div className="text-2xl font-bold text-gray-900">{kpis.expiredLicenses.toLocaleString('en-GB')}</div>
                            <div className="text-xs text-gray-500 mt-1">Requires attention</div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-orange-500">
                            <div className="text-sm font-medium text-gray-600 mb-1">Expiring Soon</div>
                            <div className="text-2xl font-bold text-gray-900">{kpis.expiringSoon.toLocaleString('en-GB')}</div>
                            <div className="text-xs text-gray-500 mt-1">Within 60 days</div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-yellow-500">
                            <div className="text-sm font-medium text-gray-600 mb-1">With Penalty Points</div>
                            <div className="text-2xl font-bold text-gray-900">{kpis.withPenaltyPoints.toLocaleString('en-GB')}</div>
                            <div className="text-xs text-gray-500 mt-1">Active points</div>
                        </div>
                    </div>

                    {/* Add/Edit Form */}
                    {showForm && (
                        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4">
                                {isEditing ? 'Edit Driver' : 'Add New Driver'}
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Branch</label>
                                    <select
                                        name="branch_id"
                                        value={formData.branch_id}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                        disabled={isEditing}
                                    >
                                        <option value="">Select Branch</option>
                                        {branches.map(branch => (
                                            <option key={branch.MamCode} value={branch.MamCode}>
                                                {branch.Branch}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Staff</label>
                                    <select
                                        name="staff_id"
                                        value={formData.staff_id}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                        disabled={isEditing || !filteredOperators.length}
                                    >
                                        <option value="">Select Staff</option>
                                        {filteredOperators.map(operator => (
                                            <option key={operator.staffId} value={operator.staffId}>
                                                {operator.Name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">License Number</label>
                                    <input
                                        type="text"
                                        name="license_number"
                                        value={formData.license_number}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">License Expiry</label>
                                    <input
                                        type="date"
                                        name="license_expiry"
                                        value={formData.license_expiry}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Penalty Points</label>
                                    <input
                                        type="number"
                                        name="penalty_points"
                                        value={formData.penalty_points}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>
                            <div className="mt-6 flex justify-end gap-3">
                                <button
                                    onClick={resetForm}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSave}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    {isEditing ? 'Update Driver' : 'Save Driver'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Drivers Table */}
                    {isLoading ? (
                        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-12 text-center">
                            <p className="text-gray-600">Loading drivers...</p>
                        </div>
                    ) : (
                        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Branch</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Name</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">License Number</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">License Expiry</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Penalty Points</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {drivers.length > 0 ? (
                                            drivers.map((driver) => {
                                                const expiryDate = driver.license_expiry ? new Date(driver.license_expiry) : null;
                                                const isExpired = expiryDate && expiryDate < new Date();
                                                const isExpiringSoon = expiryDate && (expiryDate - new Date()) / (1000 * 60 * 60 * 24) <= 60 && (expiryDate - new Date()) / (1000 * 60 * 60 * 24) >= 0;
                                                
                                                return (
                                                    <tr key={driver.staff_id} className="hover:bg-gray-50 transition-colors">
                                                        <td className="px-4 py-3 text-sm text-gray-900">{driver.branch || 'N/A'}</td>
                                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{getStaffName(driver.staff_id)}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-900">{driver.license_number || 'N/A'}</td>
                                                        <td className="px-4 py-3 text-sm">
                                                            {driver.license_expiry ? (
                                                                <span className={isExpired ? 'text-red-600 font-semibold' : isExpiringSoon ? 'text-orange-600 font-semibold' : 'text-gray-900'}>
                                                                    {driver.license_expiry.split('T')[0]}
                                                                </span>
                                                            ) : 'N/A'}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm">
                                                            {driver.penalty_points > 0 ? (
                                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                                    {driver.penalty_points}
                                                                </span>
                                                            ) : (
                                                                <span className="text-gray-500">0</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm">
                                                            <div className="flex items-center gap-2">
                                                                <button 
                                                                    onClick={() => handleEdit(driver)} 
                                                                    className="text-blue-600 hover:text-blue-800 transition-colors"
                                                                >
                                                                    <PencilSquareIcon className="w-5 h-5" />
                                                                </button>
                                                                <button 
                                                                    onClick={() => handleDelete(driver.staff_id)} 
                                                                    className="text-red-600 hover:text-red-800 transition-colors"
                                                                >
                                                                    <TrashIcon className="w-5 h-5" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="px-4 py-8 text-center text-gray-500">No drivers found.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
